// Guide page translations - separated from component for i18n consistency
// Icons are mapped separately in GuidePage.jsx (not translatable content)
export const guideTranslations = {
  en: {
    title: "User Guide",
    subtitle: "Complete guide to A2O SNS Analytics",
    tableOfContents: "Table of Contents",
    features: "Features",
    expandAll: "Expand All",
    collapseAll: "Collapse All",
    downloadPdf: "Download PDF",
    generatingPdf: "Generating PDF...",
    pdfSuccess: "PDF downloaded successfully!",
    pdfError: "Failed to generate PDF",
    proTip: "Pro Tip",
    supportedPlatforms: "Supported Platforms",
    sections: {
      login: {
        title: "Login",
        description: "Secure access code authentication system",
        details: [
          "Enter the access code provided by administrator",
          "Access codes are in the format: A2O-ROLE-2026-XXXXXXXX",
          "Session is maintained for 7 days with auto-login",
          "Admins can create and manage access codes in Settings"
        ],
        tips: ["Contact your administrator if you don't have an access code"]
      },
      dashboard: {
        title: "Dashboard Overview",
        description: "Main dashboard with key metrics at a glance",
        details: [
          "View total views, likes, comments, and shares across all platforms",
          "See engagement trends over time with interactive charts",
          "Filter data by date range, platform, and account",
          "Top performing and underperforming posts are highlighted"
        ],
        tips: ["Use date filters to compare performance across different periods"]
      },
      accounts: {
        title: "Accounts",
        description: "Detailed analysis by social media account",
        details: [
          "View all connected social media accounts in one place",
          "Compare follower counts, post counts, and engagement scores",
          "Platform-specific engagement: % for YouTube/TikTok, weighted scores for Chinese platforms",
          "Click on account cards to see detailed post performance"
        ],
        tips: ["Weighted engagement scores for Xiaohongshu/Weibo/Douyin use: likes\u00d71 + comments\u00d72 + shares\u00d71.5"]
      },
      keywordSearch: {
        title: "Keyword Search",
        description: "Search and analyze content across platforms",
        details: [
          "Search content by keyword across YouTube, TikTok, and Instagram",
          "View trending topics in the 'Trending Now' section",
          "Track keyword performance over time",
          "Data is automatically refreshed every 24 hours"
        ],
        tips: ["Add frequently monitored keywords to tracking for automated updates"]
      },
      competitorTracking: {
        title: "Competitor Tracking",
        description: "Monitor and compare competitor content performance",
        details: [
          "Add competitor content URLs to track their performance",
          "Compare engagement metrics between your content and competitors",
          "Get growth rate analysis over time",
          "Automatic daily tracking with historical data"
        ],
        tips: ["Track top-performing competitor content to learn from their strategies"]
      },
      followerGrowth: {
        title: "Follower Growth",
        description: "Track follower changes over time",
        details: [
          "View daily follower snapshots for all accounts",
          "Interactive chart showing follower trends",
          "See growth/decline percentages",
          "Compare follower growth across different platforms"
        ],
        tips: ["Snapshots are taken daily at 00:00 CST. Allow 2+ days for trend data."]
      },
      contentGrowth: {
        title: "Content Growth",
        description: "Analyze content performance trends",
        details: [
          "Track views, likes, comments, and shares growth for each post",
          "Compare period-over-period performance",
          "Identify content with accelerating or declining engagement",
          "Click on posts to view detailed growth charts"
        ],
        tips: ["Use the trend chart modal to identify content performance patterns"]
      },
      posts: {
        title: "Posts Analytics",
        description: "Comprehensive post performance data",
        details: [
          "View all posts with detailed engagement metrics",
          "Filter by platform, account, and date range",
          "Sort by views, likes, comments, or engagement rate",
          "Click to analyze individual post language distribution"
        ],
        tips: ["Chinese platforms show '-' for views when data is unavailable"]
      },
      comments: {
        title: "Comments Analysis",
        description: "AI-powered comment sentiment and keyword analysis",
        details: [
          "Analyze comments using GPT-5.2 for Western platforms",
          "Chinese NLP (jieba) for Weibo, Bilibili, Xiaohongshu, Douyin",
          "View sentiment distribution: positive, negative, neutral",
          "Extract top keywords and topics from comments"
        ],
        tips: ["Use 'Database Mode' for faster searches on previously synced comments"]
      },
      commentSearch: {
        title: "Comment Search",
        description: "Search specific keywords in comments",
        details: [
          "Search comments by keyword across all platforms",
          "Two modes: Real-time API search or Database search",
          "Sync comments to database for faster repeated searches",
          "View comment context with platform and post information"
        ],
        tips: ["Sync comments to database once, then use fast database search mode"]
      },
      crossPlatform: {
        title: "Cross-Platform Comparison",
        description: "Compare the same content across different platforms",
        details: [
          "Use Auto-Match to find similar content across platforms",
          "Compare sentiment analysis between YouTube (global) and Bilibili (China)",
          "View common and unique keywords by platform",
          "Get AI-generated insights on platform differences"
        ],
        tips: ["Auto-match works best with posts that share similar titles or keywords"]
      },
      aiInsights: {
        title: "AI Insights",
        description: "GPT-5.2 powered performance recommendations",
        details: [
          "Generate comprehensive performance analysis",
          "Get content strategy recommendations",
          "Identify anomalies and trends",
          "Platform-specific insights for Chinese vs Western audiences"
        ],
        tips: ["Generate insights weekly to track changes in content performance"]
      },
      settings: {
        title: "Settings",
        description: "Configure API credentials and system options",
        details: [
          "Manage API credentials for all platforms",
          "View and control daily sync schedules",
          "Monitor API status (TikHub, Just One API)",
          "Admin: Create and manage access codes"
        ],
        tips: ["Check API status regularly to ensure data sync is working properly"]
      },
      syncLogs: {
        title: "Sync Logs",
        description: "Monitor data synchronization status and history",
        details: [
          "View real-time sync status for all connected accounts",
          "Monitor sync history with success/failure indicators",
          "Track sync timing, duration, and data volume per platform",
          "Identify and troubleshoot failed sync operations"
        ],
        tips: ["Check sync logs if dashboard data appears outdated or incomplete"]
      },
      artistGroups: {
        title: "Artist Groups",
        description: "Manage multi-platform account groupings for cross-platform analysis",
        details: [
          "Create artist groups by combining accounts across different platforms",
          "Groups require at least 2 platforms for spillover analysis",
          "Manage group membership: add or remove accounts anytime",
          "Groups are used by Spillover Analysis and Reports features"
        ],
        tips: ["Group all accounts belonging to the same artist or brand for meaningful spillover analysis"]
      },
      spillover: {
        title: "Spillover Analysis",
        description: "Measure cross-platform follower impact from viral posts",
        details: [
          "[Purpose] Estimates how a viral post on one platform affects follower growth on other platforms within the same artist group",
          "[Viral Detection] A post is viral when likes OR views reach 2x the account's recent average (min. 3 posts required). Viral Score = max(likes/avg, views/avg)",
          "[Measurement] Compares target platform follower snapshots before and after the viral post (7-day observation window). Observed Delta = followers_after - followers_before",
          "[Baseline Adjustment] Subtracts natural growth using equal-length prior period daily growth rate. Adjusted Lift = Observed Delta - Expected Growth",
          "[Confidence Score] 0-100 data quality score. Deductions: snapshot gap (max -24), measurement delay (max -18), competing viral events (max -30 each), missing baseline (-10). Grades: High (75+), Medium (50-74), Low (<50)",
          "[Pair Summary] Aggregates by source-to-target platform pair: event count, positive ratio, total/average adjusted lift",
          "[Playbook] Top 3 priority flows, watchout flows, and operating rules with recommended content packaging per target platform",
          "[Note] Figures are directional estimates, not causal proof. External factors (news, collaborations, algorithm changes) are not reflected"
        ],
        tips: ["Focus on platform pairs with High confidence and consistent positive ratios for strategic decisions"]
      },
      correlation: {
        title: "Correlation Analysis",
        description: "Analyze relationship between individual posts and follower growth",
        details: [
          "[Purpose] Measures impact of individual posts on their own account's follower growth (same-account effect, vs. Spillover's cross-platform effect)",
          "[Impact Score] Proportional Attribution: overlapping posts are grouped and total follower growth is distributed by engagement weight (likes + comments) to prevent double-counting",
          "[Baseline] Daily follower growth on quiet days (no posts within +/-2 days) is the natural growth rate. Excess growth = actual - baseline expected",
          "[Conversion Rate] (Attributed growth / views) x 1,000 \u2014 follower conversions per 1,000 views",
          "[Correlation] Pearson coefficient (-1 to +1) between views, likes, comments, engagement rate and follower delta. Requires 3+ data points",
          "[Category Analysis] AI (GPT-5.2) auto-categorizes posts by title, then compares average performance by category",
          "[AI Summary] Executive summary, top performing content, platform insights, and optimal posting strategy recommendations"
        ],
        tips: ["Compare impact scores across content categories to find which content types drive the most follower growth"]
      },
      reports: {
        title: "Reports",
        description: "Automated spillover analysis reports with AI-generated strategy",
        details: [
          "Generate weekly or monthly spillover analysis reports",
          "Reports include core diagnosis, platform flow opportunities, and content packaging recommendations",
          "AI (GPT-5.2) generates executive-ready strategy based on actual spillover data",
          "View report history and compare across different periods",
          "Reports are generated in structured markdown format"
        ],
        tips: ["Generate monthly reports to track how spillover patterns evolve over time"]
      }
    },
    platformList: [
      { name: "YouTube", color: "#FF0000", features: "Full API" },
      { name: "TikTok", color: "#69C9D0", features: "Full API" },
      { name: "Instagram", color: "#E1306C", features: "Just One API" },
      { name: "Weibo", color: "#E6162D", features: "Just One API" },
      { name: "Xiaohongshu", color: "#FF2442", features: "TikHub API" },
      { name: "Douyin", color: "#25F4EE", features: "TikHub API" },
      { name: "Bilibili", color: "#00A1D6", features: "Just One API" }
    ]
  },
  ko: {
    title: "\uc0ac\uc6a9\uc790 \uac00\uc774\ub4dc",
    subtitle: "A2O SNS Analytics \uc644\ubcbd \uac00\uc774\ub4dc",
    tableOfContents: "\ubaa9\ucc28",
    features: "\uae30\ub2a5 \uc18c\uac1c",
    expandAll: "\ubaa8\ub450 \ud3bc\uce58\uae30",
    collapseAll: "\ubaa8\ub450 \uc811\uae30",
    downloadPdf: "PDF \ub2e4\uc6b4\ub85c\ub4dc",
    generatingPdf: "PDF \uc0dd\uc131 \uc911...",
    pdfSuccess: "PDF \ub2e4\uc6b4\ub85c\ub4dc \uc644\ub8cc!",
    pdfError: "PDF \uc0dd\uc131 \uc2e4\ud328",
    proTip: "\ud504\ub85c \ud301",
    supportedPlatforms: "\uc9c0\uc6d0 \ud50c\ub7ab\ud3fc",
    sections: {
      login: {
        title: "\ub85c\uadf8\uc778",
        description: "\ubcf4\uc548 \uc561\uc138\uc2a4 \ucf54\ub4dc \uc778\uc99d \uc2dc\uc2a4\ud15c",
        details: [
          "\uad00\ub9ac\uc790\uac00 \uc81c\uacf5\ud55c \uc561\uc138\uc2a4 \ucf54\ub4dc\ub97c \uc785\ub825\ud558\uc138\uc694",
          "\ucf54\ub4dc \ud615\uc2dd: A2O-ROLE-2026-XXXXXXXX",
          "7\uc77c\uac04 \uc790\ub3d9 \ub85c\uadf8\uc778\uc774 \uc720\uc9c0\ub429\ub2c8\ub2e4",
          "\uad00\ub9ac\uc790\ub294 \uc124\uc815\uc5d0\uc11c \ucf54\ub4dc\ub97c \uc0dd\uc131/\uad00\ub9ac\ud560 \uc218 \uc788\uc2b5\ub2c8\ub2e4"
        ],
        tips: ["\uc561\uc138\uc2a4 \ucf54\ub4dc\uac00 \uc5c6\uc73c\uba74 \uad00\ub9ac\uc790\uc5d0\uac8c \ubb38\uc758\ud558\uc138\uc694"]
      },
      dashboard: {
        title: "\ub300\uc2dc\ubcf4\ub4dc \uac1c\uc694",
        description: "\uc8fc\uc694 \uc9c0\ud45c\ub97c \ud55c\ub208\uc5d0 \ubcf4\ub294 \uba54\uc778 \ub300\uc2dc\ubcf4\ub4dc",
        details: [
          "\ubaa8\ub4e0 \ud50c\ub7ab\ud3fc\uc758 \ucd1d \uc870\ud68c\uc218, \uc88b\uc544\uc694, \ub313\uae00, \uacf5\uc720 \uc218 \ud655\uc778",
          "\uc778\ud130\ub799\ud2f0\ube0c \ucc28\ud2b8\ub85c \ucc38\uc5ec\ub3c4 \ud2b8\ub80c\ub4dc \ubd84\uc11d",
          "\ub0a0\uc9dc \ubc94\uc704, \ud50c\ub7ab\ud3fc, \uacc4\uc815\ubcc4 \ud544\ud130\ub9c1",
          "\uc131\uacfc \uc88b\uc740 \uac8c\uc2dc\ubb3c\uacfc \uc8fc\uc758\uac00 \ud544\uc694\ud55c \uac8c\uc2dc\ubb3c \ud558\uc774\ub77c\uc774\ud2b8"
        ],
        tips: ["\ub0a0\uc9dc \ud544\ud130\ub97c \uc0ac\uc6a9\ud574 \uae30\uac04\ubcc4 \uc131\uacfc\ub97c \ube44\uad50\ud574\ubcf4\uc138\uc694"]
      },
      accounts: {
        title: "\uacc4\uc815\ubcc4 \ubd84\uc11d",
        description: "\uc18c\uc15c \ubbf8\ub514\uc5b4 \uacc4\uc815\ubcc4 \uc0c1\uc138 \ubd84\uc11d",
        details: [
          "\uc5f0\uacb0\ub41c \ubaa8\ub4e0 \uc18c\uc15c \ubbf8\ub514\uc5b4 \uacc4\uc815\uc744 \ud55c\uacf3\uc5d0\uc11c \ud655\uc778",
          "\ud314\ub85c\uc6cc \uc218, \uac8c\uc2dc\ubb3c \uc218, \ucc38\uc5ec\ub3c4 \uc810\uc218 \ube44\uad50",
          "\ud50c\ub7ab\ud3fc\ubcc4 \ucc38\uc5ec\ub3c4: YouTube/TikTok\uc740 %, \uc911\uad6d \ud50c\ub7ab\ud3fc\uc740 \uac00\uc911\uce58 \uc810\uc218",
          "\uacc4\uc815 \uce74\ub4dc\ub97c \ud074\ub9ad\ud558\uba74 \uc0c1\uc138 \uac8c\uc2dc\ubb3c \uc131\uacfc \ud655\uc778"
        ],
        tips: ["\uc0e4\uc624\ud64d\uc288/\uc6e8\uc774\ubcf4/\ub354\uc6b0\uc778\uc758 \uac00\uc911\uce58 \ucc38\uc5ec\ub3c4: \uc88b\uc544\uc694\u00d71 + \ub313\uae00\u00d72 + \uacf5\uc720\u00d71.5"]
      },
      keywordSearch: {
        title: "\ud0a4\uc6cc\ub4dc \uac80\uc0c9",
        description: "\ud50c\ub7ab\ud3fc \uc804\ubc18\uc758 \ucf58\ud150\uce20 \uac80\uc0c9 \ubc0f \ubd84\uc11d",
        details: [
          "YouTube, TikTok, Instagram\uc5d0\uc11c \ud0a4\uc6cc\ub4dc\ub85c \ucf58\ud150\uce20 \uac80\uc0c9",
          "'\uc778\uae30 \uae09\uc0c1\uc2b9' \uc139\uc158\uc5d0\uc11c \ud2b8\ub80c\ub529 \ud1a0\ud53d \ud655\uc778",
          "\uc2dc\uac04\uc5d0 \ub530\ub978 \ud0a4\uc6cc\ub4dc \uc131\uacfc \ucd94\uc801",
          "24\uc2dc\uac04\ub9c8\ub2e4 \ub370\uc774\ud130 \uc790\ub3d9 \uac31\uc2e0"
        ],
        tips: ["\uc790\uc8fc \ubaa8\ub2c8\ud130\ub9c1\ud558\ub294 \ud0a4\uc6cc\ub4dc\ub97c \ucd94\uc801\uc5d0 \ucd94\uac00\ud558\uba74 \uc790\ub3d9 \uc5c5\ub370\uc774\ud2b8\ub429\ub2c8\ub2e4"]
      },
      competitorTracking: {
        title: "\uacbd\uc7c1\uc0ac \ucd94\uc801",
        description: "\uacbd\uc7c1\uc0ac \ucf58\ud150\uce20 \uc131\uacfc \ubaa8\ub2c8\ud130\ub9c1 \ubc0f \ube44\uad50",
        details: [
          "\uacbd\uc7c1\uc0ac \ucf58\ud150\uce20 URL\uc744 \ucd94\uac00\ud558\uc5ec \uc131\uacfc \ucd94\uc801",
          "\ub0b4 \ucf58\ud150\uce20\uc640 \uacbd\uc7c1\uc0ac \uac04 \ucc38\uc5ec\ub3c4 \uc9c0\ud45c \ube44\uad50",
          "\uc2dc\uac04\uc5d0 \ub530\ub978 \uc131\uc7a5\ub960 \ubd84\uc11d",
          "\ud788\uc2a4\ud1a0\ub9ac \ub370\uc774\ud130\uc640 \ud568\uaed8 \uc790\ub3d9 \uc77c\uc77c \ucd94\uc801"
        ],
        tips: ["\ucd5c\uace0 \uc131\uacfc \uacbd\uc7c1\uc0ac \ucf58\ud150\uce20\ub97c \ucd94\uc801\ud558\uc5ec \uc804\ub7b5\uc744 \ubc30\uc6cc\ubcf4\uc138\uc694"]
      },
      followerGrowth: {
        title: "\ud314\ub85c\uc6cc \uc131\uc7a5",
        description: "\uc2dc\uac04\uc5d0 \ub530\ub978 \ud314\ub85c\uc6cc \ubcc0\ud654 \ucd94\uc801",
        details: [
          "\ubaa8\ub4e0 \uacc4\uc815\uc758 \uc77c\uc77c \ud314\ub85c\uc6cc \uc2a4\ub0c5\uc0f7 \ud655\uc778",
          "\ud314\ub85c\uc6cc \ud2b8\ub80c\ub4dc\ub97c \ubcf4\uc5ec\uc8fc\ub294 \uc778\ud130\ub799\ud2f0\ube0c \ucc28\ud2b8",
          "\uc99d\uac00/\uac10\uc18c \ube44\uc728 \ud655\uc778",
          "\ud50c\ub7ab\ud3fc\ubcc4 \ud314\ub85c\uc6cc \uc131\uc7a5 \ube44\uad50"
        ],
        tips: ["\uc2a4\ub0c5\uc0f7\uc740 \ub9e4\uc77c CST 00:00\uc5d0 \ucd2c\uc601\ub429\ub2c8\ub2e4. \ud2b8\ub80c\ub4dc \ub370\uc774\ud130\ub97c \uc704\ud574 2\uc77c \uc774\uc0c1 \uae30\ub2e4\ub824\uc8fc\uc138\uc694."]
      },
      contentGrowth: {
        title: "\ucf58\ud150\uce20 \uc131\uc7a5",
        description: "\ucf58\ud150\uce20 \uc131\uacfc \ud2b8\ub80c\ub4dc \ubd84\uc11d",
        details: [
          "\uac01 \uac8c\uc2dc\ubb3c\uc758 \uc870\ud68c\uc218, \uc88b\uc544\uc694, \ub313\uae00, \uacf5\uc720 \uc131\uc7a5 \ucd94\uc801",
          "\uae30\uac04\ubcc4 \uc131\uacfc \ube44\uad50",
          "\ucc38\uc5ec\ub3c4\uac00 \uc99d\uac00 \ub610\ub294 \uac10\uc18c\ud558\ub294 \ucf58\ud150\uce20 \uc2dd\ubcc4",
          "\uac8c\uc2dc\ubb3c \ud074\ub9ad \uc2dc \uc0c1\uc138 \uc131\uc7a5 \ucc28\ud2b8 \ud655\uc778"
        ],
        tips: ["\ud2b8\ub80c\ub4dc \ucc28\ud2b8 \ubaa8\ub2ec\uc744 \uc0ac\uc6a9\ud574 \ucf58\ud150\uce20 \uc131\uacfc \ud328\ud134\uc744 \ud30c\uc545\ud558\uc138\uc694"]
      },
      posts: {
        title: "\uac8c\uc2dc\ubb3c \ubd84\uc11d",
        description: "\uc885\ud569\uc801\uc778 \uac8c\uc2dc\ubb3c \uc131\uacfc \ub370\uc774\ud130",
        details: [
          "\uc0c1\uc138 \ucc38\uc5ec\ub3c4 \uc9c0\ud45c\uc640 \ud568\uaed8 \ubaa8\ub4e0 \uac8c\uc2dc\ubb3c \ud655\uc778",
          "\ud50c\ub7ab\ud3fc, \uacc4\uc815, \ub0a0\uc9dc \ubc94\uc704\ubcc4 \ud544\ud130\ub9c1",
          "\uc870\ud68c\uc218, \uc88b\uc544\uc694, \ub313\uae00, \ucc38\uc5ec\uc728\ub85c \uc815\ub82c",
          "\ud074\ub9ad\ud558\uc5ec \uac1c\ubcc4 \uac8c\uc2dc\ubb3c \uc5b8\uc5b4 \ubd84\ud3ec \ubd84\uc11d"
        ],
        tips: ["\uc911\uad6d \ud50c\ub7ab\ud3fc\uc740 \uc870\ud68c\uc218 \ub370\uc774\ud130\uac00 \uc5c6\uc744 \ub54c '-'\ub85c \ud45c\uc2dc\ub429\ub2c8\ub2e4"]
      },
      comments: {
        title: "\ub313\uae00 \ubd84\uc11d",
        description: "AI \uae30\ubc18 \ub313\uae00 \uac10\uc131 \ubc0f \ud0a4\uc6cc\ub4dc \ubd84\uc11d",
        details: [
          "\uc11c\uc591 \ud50c\ub7ab\ud3fc\uc740 GPT-5.2\ub97c \uc0ac\uc6a9\ud574 \ub313\uae00 \ubd84\uc11d",
          "\uc6e8\uc774\ubcf4, \ube4c\ub9ac\ube4c\ub9ac, \uc0e4\uc624\ud64d\uc218, \ub354\uc6b0\uc778\uc740 \uc911\uad6d\uc5b4 NLP(jieba) \uc0ac\uc6a9",
          "\uac10\uc131 \ubd84\ud3ec \ud655\uc778: \uae0d\uc815, \ubd80\uc815, \uc911\ub9bd",
          "\ub313\uae00\uc5d0\uc11c \uc0c1\uc704 \ud0a4\uc6cc\ub4dc \ubc0f \ud1a0\ud53d \ucd94\ucd9c"
        ],
        tips: ["\uc774\uc804\uc5d0 \ub3d9\uae30\ud654\ud55c \ub313\uae00\uc758 \ube60\ub978 \uac80\uc0c9\uc744 \uc704\ud574 '\ub370\uc774\ud130\ubca0\uc774\uc2a4 \ubaa8\ub4dc'\ub97c \uc0ac\uc6a9\ud558\uc138\uc694"]
      },
      commentSearch: {
        title: "\ub313\uae00 \uac80\uc0c9",
        description: "\ub313\uae00\uc5d0\uc11c \ud2b9\uc815 \ud0a4\uc6cc\ub4dc \uac80\uc0c9",
        details: [
          "\ubaa8\ub4e0 \ud50c\ub7ab\ud3fc\uc5d0\uc11c \ud0a4\uc6cc\ub4dc\ub85c \ub313\uae00 \uac80\uc0c9",
          "\ub450 \uac00\uc9c0 \ubaa8\ub4dc: \uc2e4\uc2dc\uac04 API \uac80\uc0c9 \ub610\ub294 \ub370\uc774\ud130\ubca0\uc774\uc2a4 \uac80\uc0c9",
          "\ubc18\ubcf5 \uac80\uc0c9\uc744 \uc704\ud574 \ub313\uae00\uc744 \ub370\uc774\ud130\ubca0\uc774\uc2a4\uc5d0 \ub3d9\uae30\ud654",
          "\ud50c\ub7ab\ud3fc \ubc0f \uac8c\uc2dc\ubb3c \uc815\ubcf4\uc640 \ud568\uaed8 \ub313\uae00 \ucee8\ud14d\uc2a4\ud2b8 \ud655\uc778"
        ],
        tips: ["\ub313\uae00\uc744 \ud55c \ubc88 \ub3d9\uae30\ud654\ud55c \ud6c4 \ube60\ub978 \ub370\uc774\ud130\ubca0\uc774\uc2a4 \uac80\uc0c9 \ubaa8\ub4dc\ub97c \uc0ac\uc6a9\ud558\uc138\uc694"]
      },
      crossPlatform: {
        title: "\ud06c\ub85c\uc2a4 \ud50c\ub7ab\ud3fc \ube44\uad50",
        description: "\uc11c\ub85c \ub2e4\ub978 \ud50c\ub7ab\ud3fc\uc5d0\uc11c \uac19\uc740 \ucf58\ud150\uce20 \ube44\uad50",
        details: [
          "Auto-Match\ub85c \ud50c\ub7ab\ud3fc \uac04 \uc720\uc0ac \ucf58\ud150\uce20 \ucc3e\uae30",
          "YouTube(\uae00\ub85c\ubc8c)\uc640 Bilibili(\uc911\uad6d) \uac04 \uac10\uc131 \ubd84\uc11d \ube44\uad50",
          "\ud50c\ub7ab\ud3fc\ubcc4 \uacf5\ud1b5 \ubc0f \uace0\uc720 \ud0a4\uc6cc\ub4dc \ud655\uc778",
          "\ud50c\ub7ab\ud3fc \ucc28\uc774\uc5d0 \ub300\ud55c AI \uc778\uc0ac\uc774\ud2b8 \uc0dd\uc131"
        ],
        tips: ["Auto-match\ub294 \uc720\uc0ac\ud55c \uc81c\ubaa9\uc774\ub098 \ud0a4\uc6cc\ub4dc\ub97c \uacf5\uc720\ud558\ub294 \uac8c\uc2dc\ubb3c\uc5d0\uc11c \uac00\uc7a5 \uc798 \uc791\ub3d9\ud569\ub2c8\ub2e4"]
      },
      aiInsights: {
        title: "AI \uc778\uc0ac\uc774\ud2b8",
        description: "GPT-5.2 \uae30\ubc18 \uc131\uacfc \ubd84\uc11d \ubc0f \ucd94\ucc9c",
        details: [
          "\uc885\ud569\uc801\uc778 \uc131\uacfc \ubd84\uc11d \uc0dd\uc131",
          "\ucf58\ud150\uce20 \uc804\ub7b5 \ucd94\ucc9c \ubc1b\uae30",
          "\uc774\uc0c1 \uc9d5\ud6c4 \ubc0f \ud2b8\ub80c\ub4dc \uc2dd\ubcc4",
          "\uc911\uad6d vs \uc11c\uc591 \uc624\ub514\uc5b8\uc2a4\ub97c \uc704\ud55c \ud50c\ub7ab\ud3fc\ubcc4 \uc778\uc0ac\uc774\ud2b8"
        ],
        tips: ["\ucf58\ud150\uce20 \uc131\uacfc \ubcc0\ud654\ub97c \ucd94\uc801\ud558\uae30 \uc704\ud574 \uc8fc\uac04 \uc778\uc0ac\uc774\ud2b8\ub97c \uc0dd\uc131\ud558\uc138\uc694"]
      },
      settings: {
        title: "\uc124\uc815",
        description: "API \uc790\uaca9 \uc99d\uba85 \ubc0f \uc2dc\uc2a4\ud15c \uc635\uc158 \uad6c\uc131",
        details: [
          "\ubaa8\ub4e0 \ud50c\ub7ab\ud3fc\uc758 API \uc790\uaca9 \uc99d\uba85 \uad00\ub9ac",
          "\uc77c\uc77c \ub3d9\uae30\ud654 \uc77c\uc815 \ud655\uc778 \ubc0f \uc81c\uc5b4",
          "API \uc0c1\ud0dc \ubaa8\ub2c8\ud130\ub9c1 (TikHub, Just One API)",
          "\uad00\ub9ac\uc790: \uc561\uc138\uc2a4 \ucf54\ub4dc \uc0dd\uc131 \ubc0f \uad00\ub9ac"
        ],
        tips: ["\ub370\uc774\ud130 \ub3d9\uae30\ud654\uac00 \uc81c\ub300\ub85c \uc791\ub3d9\ud558\ub294\uc9c0 \uc815\uae30\uc801\uc73c\ub85c API \uc0c1\ud0dc\ub97c \ud655\uc778\ud558\uc138\uc694"]
      },
      syncLogs: {
        title: "\ub3d9\uae30\ud654 \ub85c\uadf8",
        description: "\ub370\uc774\ud130 \ub3d9\uae30\ud654 \uc0c1\ud0dc \ubc0f \uc774\ub825 \ubaa8\ub2c8\ud130\ub9c1",
        details: [
          "\uc5f0\uacb0\ub41c \ubaa8\ub4e0 \uacc4\uc815\uc758 \uc2e4\uc2dc\uac04 \ub3d9\uae30\ud654 \uc0c1\ud0dc \ud655\uc778",
          "\uc131\uacf5/\uc2e4\ud328 \uc9c0\uc2dc\uc790\uc640 \ud568\uaed8 \ub3d9\uae30\ud654 \uc774\ub825 \ubaa8\ub2c8\ud130\ub9c1",
          "\ud50c\ub7ab\ud3fc\ubcc4 \ub3d9\uae30\ud654 \uc2dc\uac01, \uc18c\uc694 \uc2dc\uac04, \ub370\uc774\ud130 \ubcfc\ub968 \ucd94\uc801",
          "\uc2e4\ud328\ud55c \ub3d9\uae30\ud654 \uc791\uc5c5\uc758 \uc6d0\uc778 \ud30c\uc545 \ubc0f \ubb38\uc81c \ud574\uacb0"
        ],
        tips: ["\ub300\uc2dc\ubcf4\ub4dc \ub370\uc774\ud130\uac00 \uc624\ub798\ub41c \uac83\uc73c\ub85c \ubcf4\uc774\uba74 \ub3d9\uae30\ud654 \ub85c\uadf8\ub97c \ud655\uc778\ud558\uc138\uc694"]
      },
      artistGroups: {
        title: "\uc544\ud2f0\uc2a4\ud2b8 \uadf8\ub8f9",
        description: "\ud06c\ub85c\uc2a4 \ud50c\ub7ab\ud3fc \ubd84\uc11d\uc744 \uc704\ud55c \uba40\ud2f0 \ud50c\ub7ab\ud3fc \uacc4\uc815 \uadf8\ub8f9 \uad00\ub9ac",
        details: [
          "\uc11c\ub85c \ub2e4\ub978 \ud50c\ub7ab\ud3fc\uc758 \uacc4\uc815\uc744 \uacb0\ud569\ud558\uc5ec \uc544\ud2f0\uc2a4\ud2b8 \uadf8\ub8f9 \uc0dd\uc131",
          "\uc2a4\ud544\uc624\ubc84 \ubd84\uc11d\uc744 \uc704\ud574 \ucd5c\uc18c 2\uac1c \uc774\uc0c1\uc758 \ud50c\ub7ab\ud3fc\uc774 \ud544\uc694",
          "\uadf8\ub8f9 \uba64\ubc84\uc2ed \uad00\ub9ac: \uc5b8\uc81c\ub4e0 \uacc4\uc815 \ucd94\uac00/\uc0ad\uc81c \uac00\ub2a5",
          "\uc2a4\ud544\uc624\ubc84 \ubd84\uc11d \ubc0f \ub9ac\ud3ec\ud2b8 \uae30\ub2a5\uc5d0\uc11c \uc0ac\uc6a9\ub428"
        ],
        tips: ["\uac19\uc740 \uc544\ud2f0\uc2a4\ud2b8\ub098 \ube0c\ub79c\ub4dc\uc758 \ubaa8\ub4e0 \uacc4\uc815\uc744 \uadf8\ub8f9\uc73c\ub85c \ubb36\uc5b4\uc57c \uc758\ubbf8 \uc788\ub294 \uc2a4\ud544\uc624\ubc84 \ubd84\uc11d\uc774 \uac00\ub2a5\ud569\ub2c8\ub2e4"]
      },
      spillover: {
        title: "\uc2a4\ud544\uc624\ubc84 \ubd84\uc11d",
        description: "\ubc14\uc774\ub7fc \uac8c\uc2dc\ubb3c\uc758 \ud06c\ub85c\uc2a4 \ud50c\ub7ab\ud3fc \ud314\ub85c\uc6cc \ud30c\uae09\ud6a8\uacfc \uce21\uc815",
        details: [
          "[\ubaa9\uc801] \ud55c \ud50c\ub7ab\ud3fc\uc5d0\uc11c \ubc14\uc774\ub7fc \uac8c\uc2dc\ubb3c\uc774 \ubc1c\uc0dd\ud588\uc744 \ub54c, \uac19\uc740 \uc544\ud2f0\uc2a4\ud2b8 \uadf8\ub8f9\uc758 \ub2e4\ub978 \ud50c\ub7ab\ud3fc \ud314\ub85c\uc6cc \uc131\uc7a5\uc5d0 \ubbf8\uce58\ub294 \uc601\ud5a5\uc744 \ucd94\uc815",
          "[\ubc14\uc774\ub7fc \ud310\ubcc4] \uac19\uc740 \uacc4\uc815 \ucd5c\uadfc \ud3c9\uade0 \uc88b\uc544\uc694 \ub610\ub294 \uc870\ud68c\uc218\uc758 2\ubc30 \uc774\uc0c1\uc774\uba74 \ubc14\uc774\ub7fc\uc73c\ub85c \ubd84\ub958. \ucd5c\uc18c 3\uac1c \uac8c\uc2dc\ubb3c \ud544\uc694. \ubc14\uc774\ub7fc \uc2a4\ucf54\uc5b4 = max(\uc88b\uc544\uc694/\ud3c9\uade0, \uc870\ud68c\uc218/\ud3c9\uade0)",
          "[\uce21\uc815 \ubc29\uc2dd] \ud0c0\uac9f \ud50c\ub7ab\ud3fc\uc5d0\uc11c \ubc14\uc774\ub7fc \uac8c\uc2dc\uc77c \uc804\ud6c4\uc758 \ud314\ub85c\uc6cc \uc2a4\ub0c5\uc0f7\uc744 \ube44\uad50 (\uad00\uce21 \uc708\ub3c4\uc6b0: 7\uc77c). \uad00\uce21 \ubcc0\ud654\ub7c9 = \uac8c\uc2dc \ud6c4 \ud314\ub85c\uc6cc - \uac8c\uc2dc \uc804 \ud314\ub85c\uc6cc",
          "[\uae30\ub300 \uc131\uc7a5\ubd84 \ucc28\uac10] \uad00\uce21 \uad6c\uac04\uacfc \ub3d9\uc77c \uae38\uc774\uc758 \uc9c1\uc804 \uad6c\uac04 \uc77c\uc77c \uc131\uc7a5\ub960\uc744 \uae30\ub300\uce58\ub85c \uc0b0\ucd9c. \ubcf4\uc815\ub41c \ub9ac\ud504\ud2b8(Adjusted Lift) = \uad00\uce21 \ubcc0\ud654\ub7c9 - \uae30\ub300 \uc131\uc7a5\ubd84",
          "[\uc2e0\ub8b0\ub3c4 \uc810\uc218] 0~100\uc810\uc73c\ub85c \ub370\uc774\ud130 \ud488\uc9c8 \ud3c9\uac00. \uac10\uc810: \uc2a4\ub0c5\uc0f7 \uac2d(\ucd5c\ub300 -24), \uce21\uc815 \uc9c0\uc5f0(\ucd5c\ub300 -18), \uacbd\ud569 \ubc14\uc774\ub7fc(\uac74\ub2f9 \ucd5c\ub300 -30), \uae30\uc900\uc120 \uc5c6\uc74c(-10). \ub4f1\uae09: 75+ High, 50~74 Medium, 50\ubbf8\ub9cc Low",
          "[\uc30d\ubcc4 \uc694\uc57d] \uc18c\uc2a4->\ud0c0\uac9f \ud50c\ub7ab\ud3fc \uc30d\ubcc4 \uc9d1\uacc4: \uc774\ubca4\ud2b8 \uc218, \uae0d\uc815 \ube44\uc728, \ucd1d/\ud3c9\uade0 \ubcf4\uc815 \ub9ac\ud504\ud2b8",
          "[\uc804\ub7b5 \ud50c\ub808\uc774\ubd81] \ubcf4\uc815 \ub9ac\ud504\ud2b8 \uc0c1\uc704 3\uac1c \uc6b0\uc120 \ud750\ub984, \ubd80\uc815 \ud6a8\uacfc \uc8fc\uc758 \ud750\ub984, \ud0c0\uac9f \ud50c\ub7ab\ud3fc\ubcc4 \ucd94\ucc9c \ucf58\ud150\uce20 \ud3ec\ub9f7 \ud3ec\ud568 \uc6b4\uc601 \uaddc\uce59",
          "[\uc8fc\uc758\uc0ac\ud56d] \ubcf8 \uc218\uce58\ub294 \uc778\uacfc\uad00\uacc4\uac00 \uc544\ub2cc \ubc29\ud5a5\uc131 \ucd94\uc815 \uc9c0\ud45c\uc785\ub2c8\ub2e4. \ub274\uc2a4, \ubc29\uc1a1 \ucd9c\uc5f0, \uc54c\uace0\ub9ac\uc998 \ubcc0\uacbd \ub4f1 \uc678\ubd80 \uc694\uc778\uc740 \ubc18\uc601\ub418\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4"
        ],
        tips: ["\uc804\ub7b5\uc801 \uc758\uc0ac\uacb0\uc815\uc5d0\ub294 'High' \uc2e0\ub8b0\ub3c4\uc774\uba74\uc11c \uae0d\uc815 \ube44\uc728\uc774 \uc77c\uad00\ub41c \ud50c\ub7ab\ud3fc \uc30d\uc5d0 \uc9d1\uc911\ud558\uc138\uc694"]
      },
      correlation: {
        title: "\uc0c1\uad00\uad00\uacc4 \ubd84\uc11d",
        description: "\uac1c\ubcc4 \uac8c\uc2dc\ubb3c\uacfc \ud314\ub85c\uc6cc \uc131\uc7a5\uc758 \uad00\uacc4 \ubd84\uc11d",
        details: [
          "[\ubaa9\uc801] \uac1c\ubcc4 \uac8c\uc2dc\ubb3c\uc774 \ud574\ub2f9 \uacc4\uc815\uc758 \ud314\ub85c\uc6cc \uc131\uc7a5\uc5d0 \ubbf8\uce58\ub294 \uc601\ud5a5 \uce21\uc815 (\uc2a4\ud544\uc624\ubc84\uac00 '\ud0c0 \ud50c\ub7ab\ud3fc \ud6a8\uacfc'\ub77c\uba74, \uc0c1\uad00\uad00\uacc4 \ubd84\uc11d\uc740 '\uac19\uc740 \uacc4\uc815 \ub0b4 \ud6a8\uacfc')",
          "[\uc601\ud5a5 \uc810\uc218] \ube44\ub840 \ubc30\ubd84\ubc95: \uce21\uc815 \uc708\ub3c4\uc6b0\uac00 \uacb9\uce58\ub294 \uac8c\uc2dc\ubb3c\uc744 \uadf8\ub8f9\uc73c\ub85c \ubb36\uace0, \ucd1d \uc131\uc7a5\ubd84\uc744 \ucc38\uc5ec\ub3c4 \uac00\uc911\uce58(\uc88b\uc544\uc694+\ub313\uae00)\ub85c \ube44\ub840 \ubc30\ubd84\ud558\uc5ec \uc774\uc911 \uc9d1\uacc4 \ubc29\uc9c0",
          "[\uae30\uc900\uc120] '\uc870\uc6a9\ud55c \ub0a0'(\uac8c\uc2dc\ubb3c \uc5c6\ub294 \ub0a0 \ubc0f +-2\uc77c \uc81c\uc678)\uc758 \uc77c\uc77c \ud3c9\uade0 \ud314\ub85c\uc6cc \ubcc0\ud654\ub97c \uc790\uc5f0 \uc131\uc7a5\ub960\ub85c \uc0b0\ucd9c. \ucd08\uacfc \uc131\uc7a5 = \uc2e4\uc81c \uc131\uc7a5 - \uae30\uc900\uc120 \uae30\ub300\uce58",
          "[\uc804\ud658\uc728] (\ubc30\ubd84\ub41c \uc131\uc7a5\ub7c9 / \uc870\ud68c\uc218) x 1,000 \u2014 1,000\ud68c \uc870\ud68c\ub2f9 \ud314\ub85c\uc6cc \uc804\ud658 \uc218",
          "[\uc0c1\uad00\uacc4\uc218] \ud53c\uc5b4\uc2a8 \uc0c1\uad00\uacc4\uc218(-1~+1)\ub85c \uc870\ud68c\uc218, \uc88b\uc544\uc694, \ub313\uae00, \ucc38\uc5ec\uc728\uacfc \ud314\ub85c\uc6cc \ubcc0\ud654\uc758 \uc0c1\uad00\uad00\uacc4 \uc0b0\ucd9c. \ucd5c\uc18c 3\uac1c \ub370\uc774\ud130 \ud3ec\uc778\ud2b8 \ud544\uc694",
          "[\uce74\ud14c\uace0\ub9ac \ubd84\uc11d] AI(GPT-5.2)\uac00 \uac8c\uc2dc\ubb3c \uc81c\ubaa9 \uae30\ubc18 \uc790\ub3d9 \ubd84\ub958(\ubba4\ube44, \ud37c\ud3ec\uba3c\uc2a4, \ube44\ud558\uc778\ub4dc \ub4f1) \ud6c4 \uce74\ud14c\uace0\ub9ac\ubcc4 \ud3c9\uade0 \uc131\uacfc \ube44\uad50",
          "[AI \uc694\uc57d] \ud575\uc2ec \uc694\uc57d, \uc131\uacfc \uc0c1\uc704 \ucf58\ud150\uce20, \ud50c\ub7ab\ud3fc\ubcc4 \uc778\uc0ac\uc774\ud2b8, \ucd5c\uc801 \ud3ec\uc2a4\ud305 \uc804\ub7b5 \ub4f1 AI \ubd84\uc11d \ub9ac\ud3ec\ud2b8 \uc0dd\uc131"
        ],
        tips: ["\ucf58\ud150\uce20 \uce74\ud14c\uace0\ub9ac\ubcc4 \uc601\ud5a5 \uc810\uc218\ub97c \ube44\uad50\ud558\uc5ec \ud314\ub85c\uc6cc \uc131\uc7a5\uc5d0 \uac00\uc7a5 \ud6a8\uacfc\uc801\uc778 \ucf58\ud150\uce20 \uc720\ud615\uc744 \ud30c\uc545\ud558\uc138\uc694"]
      },
      reports: {
        title: "\ub9ac\ud3ec\ud2b8",
        description: "AI \uae30\ubc18 \uc790\ub3d9 \uc2a4\ud544\uc624\ubc84 \ubd84\uc11d \ub9ac\ud3ec\ud2b8",
        details: [
          "\uc8fc\uac04 \ub610\ub294 \uc6d4\uac04 \uc2a4\ud544\uc624\ubc84 \ubd84\uc11d \ub9ac\ud3ec\ud2b8 \uc790\ub3d9 \uc0dd\uc131",
          "\ud575\uc2ec \uc9c4\ub2e8, \uc720\ub9dd \ud50c\ub7ab\ud3fc \ud750\ub984, \ucf58\ud150\uce20 \ud328\ud0a4\uc9d5 \uc81c\uc548 \ud3ec\ud568",
          "AI(GPT-5.2)\uac00 \uc2e4\uc81c \uc2a4\ud544\uc624\ubc84 \ub370\uc774\ud130 \uae30\ubc18\uc73c\ub85c \uc804\ub7b5 \ub9ac\ud3ec\ud2b8 \uc791\uc131",
          "\ub9ac\ud3ec\ud2b8 \uc774\ub825 \uc870\ud68c \ubc0f \uae30\uac04\ubcc4 \ube44\uad50 \uac00\ub2a5",
          "\ub9c8\ud06c\ub2e4\uc6b4 \ud615\uc2dd\uc758 \uad6c\uc870\ud654\ub41c \uc139\uc158\uc73c\ub85c \uc0dd\uc131"
        ],
        tips: ["\uc6d4\uac04 \ub9ac\ud3ec\ud2b8\ub97c \uc815\uae30\uc801\uc73c\ub85c \uc0dd\uc131\ud558\uc5ec \uc2a4\ud544\uc624\ubc84 \ud328\ud134\uc758 \ubcc0\ud654\ub97c \ucd94\uc801\ud558\uc138\uc694"]
      }
    },
    platformList: [
      { name: "YouTube", color: "#FF0000", features: "\uc804\uccb4 API" },
      { name: "TikTok", color: "#69C9D0", features: "\uc804\uccb4 API" },
      { name: "Instagram", color: "#E1306C", features: "Just One API" },
      { name: "Weibo", color: "#E6162D", features: "Just One API" },
      { name: "Xiaohongshu", color: "#FF2442", features: "TikHub API" },
      { name: "Douyin", color: "#25F4EE", features: "TikHub API" },
      { name: "Bilibili", color: "#00A1D6", features: "Just One API" }
    ]
  },
  zh: {
    title: "\u7528\u6237\u6307\u5357",
    subtitle: "A2O SNS Analytics \u5b8c\u6574\u6307\u5357",
    tableOfContents: "\u76ee\u5f55",
    features: "\u529f\u80fd\u4ecb\u7ecd",
    expandAll: "\u5168\u90e8\u5c55\u5f00",
    collapseAll: "\u5168\u90e8\u6298\u53e0",
    downloadPdf: "\u4e0b\u8f7dPDF",
    generatingPdf: "\u6b63\u5728\u751f\u6210PDF...",
    pdfSuccess: "PDF\u4e0b\u8f7d\u5b8c\u6210\uff01",
    pdfError: "PDF\u751f\u6210\u5931\u8d25",
    proTip: "\u4e13\u4e1a\u63d0\u793a",
    supportedPlatforms: "\u652f\u6301\u7684\u5e73\u53f0",
    sections: {
      login: {
        title: "\u767b\u5f55",
        description: "\u5b89\u5168\u8bbf\u95ee\u7801\u8ba4\u8bc1\u7cfb\u7edf",
        details: [
          "\u8f93\u5165\u7ba1\u7406\u5458\u63d0\u4f9b\u7684\u8bbf\u95ee\u7801",
          "\u4ee3\u7801\u683c\u5f0f\uff1aA2O-ROLE-2026-XXXXXXXX",
          "\u4f1a\u8bdd\u6709\u6548\u671f7\u5929\uff0c\u652f\u6301\u81ea\u52a8\u767b\u5f55",
          "\u7ba1\u7406\u5458\u53ef\u5728\u8bbe\u7f6e\u4e2d\u521b\u5efa\u548c\u7ba1\u7406\u8bbf\u95ee\u7801"
        ],
        tips: ["\u5982\u679c\u6ca1\u6709\u8bbf\u95ee\u7801\uff0c\u8bf7\u8054\u7cfb\u7ba1\u7406\u5458"]
      },
      dashboard: {
        title: "\u4eea\u8868\u76d8\u6982\u89c8",
        description: "\u4e00\u76ee\u4e86\u7136\u7684\u5173\u952e\u6307\u6807\u4e3b\u4eea\u8868\u76d8",
        details: [
          "\u67e5\u770b\u6240\u6709\u5e73\u53f0\u7684\u603b\u6d4f\u89c8\u91cf\u3001\u70b9\u8d5e\u3001\u8bc4\u8bba\u548c\u5206\u4eab",
          "\u901a\u8fc7\u4ea4\u4e92\u5f0f\u56fe\u8868\u67e5\u770b\u53c2\u4e0e\u5ea6\u8d8b\u52bf",
          "\u6309\u65e5\u671f\u8303\u56f4\u3001\u5e73\u53f0\u548c\u8d26\u53f7\u7b5b\u9009\u6570\u636e",
          "\u9ad8\u4eae\u663e\u793a\u8868\u73b0\u6700\u4f73\u548c\u9700\u8981\u5173\u6ce8\u7684\u5e16\u5b50"
        ],
        tips: ["\u4f7f\u7528\u65e5\u671f\u7b5b\u9009\u5668\u6bd4\u8f83\u4e0d\u540c\u65f6\u671f\u7684\u8868\u73b0"]
      },
      accounts: {
        title: "\u8d26\u53f7\u5206\u6790",
        description: "\u6309\u793e\u4ea4\u5a92\u4f53\u8d26\u53f7\u8be6\u7ec6\u5206\u6790",
        details: [
          "\u5728\u4e00\u5904\u67e5\u770b\u6240\u6709\u8fde\u63a5\u7684\u793e\u4ea4\u5a92\u4f53\u8d26\u53f7",
          "\u6bd4\u8f83\u7c89\u4e1d\u6570\u3001\u5e16\u5b50\u6570\u548c\u53c2\u4e0e\u5ea6\u5206\u6570",
          "\u5e73\u53f0\u7279\u5b9a\u53c2\u4e0e\u5ea6\uff1aYouTube/TikTok\u7528%\uff0c\u4e2d\u56fd\u5e73\u53f0\u7528\u52a0\u6743\u5206\u6570",
          "\u70b9\u51fb\u8d26\u53f7\u5361\u7247\u67e5\u770b\u8be6\u7ec6\u5e16\u5b50\u8868\u73b0"
        ],
        tips: ["\u5c0f\u7ea2\u4e66/\u5fae\u535a/\u6296\u97f3\u7684\u52a0\u6743\u53c2\u4e0e\u5ea6\uff1a\u70b9\u8d5e\u00d71 + \u8bc4\u8bba\u00d72 + \u5206\u4eab\u00d71.5"]
      },
      keywordSearch: {
        title: "\u5173\u952e\u8bcd\u641c\u7d22",
        description: "\u8de8\u5e73\u53f0\u5185\u5bb9\u641c\u7d22\u4e0e\u5206\u6790",
        details: [
          "\u5728YouTube\u3001TikTok\u3001Instagram\u4e0a\u6309\u5173\u952e\u8bcd\u641c\u7d22\u5185\u5bb9",
          "\u5728'\u70ed\u95e8\u8d8b\u52bf'\u90e8\u5206\u67e5\u770b\u70ed\u95e8\u8bdd\u9898",
          "\u8ffd\u8e2a\u5173\u952e\u8bcd\u968f\u65f6\u95f4\u7684\u8868\u73b0",
          "\u6570\u636e\u6bcf24\u5c0f\u65f6\u81ea\u52a8\u5237\u65b0"
        ],
        tips: ["\u5c06\u7ecf\u5e38\u76d1\u63a7\u7684\u5173\u952e\u8bcd\u6dfb\u52a0\u5230\u8ffd\u8e2a\u5217\u8868\uff0c\u83b7\u53d6\u81ea\u52a8\u66f4\u65b0"]
      },
      competitorTracking: {
        title: "\u7ade\u4e89\u5bf9\u624b\u8ffd\u8e2a",
        description: "\u76d1\u63a7\u548c\u6bd4\u8f83\u7ade\u4e89\u5bf9\u624b\u5185\u5bb9\u8868\u73b0",
        details: [
          "\u6dfb\u52a0\u7ade\u4e89\u5bf9\u624b\u5185\u5bb9URL\u8fdb\u884c\u8ffd\u8e2a",
          "\u6bd4\u8f83\u60a8\u7684\u5185\u5bb9\u4e0e\u7ade\u4e89\u5bf9\u624b\u7684\u53c2\u4e0e\u5ea6\u6307\u6807",
          "\u83b7\u53d6\u968f\u65f6\u95f4\u53d8\u5316\u7684\u589e\u957f\u7387\u5206\u6790",
          "\u81ea\u52a8\u6bcf\u65e5\u8ffd\u8e2a\uff0c\u4fdd\u5b58\u5386\u53f2\u6570\u636e"
        ],
        tips: ["\u8ffd\u8e2a\u8868\u73b0\u6700\u597d\u7684\u7ade\u4e89\u5bf9\u624b\u5185\u5bb9\uff0c\u5b66\u4e60\u4ed6\u4eec\u7684\u7b56\u7565"]
      },
      followerGrowth: {
        title: "\u7c89\u4e1d\u589e\u957f",
        description: "\u8ffd\u8e2a\u7c89\u4e1d\u968f\u65f6\u95f4\u7684\u53d8\u5316",
        details: [
          "\u67e5\u770b\u6240\u6709\u8d26\u53f7\u7684\u6bcf\u65e5\u7c89\u4e1d\u5feb\u7167",
          "\u663e\u793a\u7c89\u4e1d\u8d8b\u52bf\u7684\u4ea4\u4e92\u5f0f\u56fe\u8868",
          "\u67e5\u770b\u589e\u957f/\u4e0b\u964d\u767e\u5206\u6bd4",
          "\u6bd4\u8f83\u4e0d\u540c\u5e73\u53f0\u7684\u7c89\u4e1d\u589e\u957f"
        ],
        tips: ["\u5feb\u7167\u6bcf\u5929CST 00:00\u62cd\u6444\u3002\u8bf7\u7b49\u5f852\u5929\u4ee5\u4e0a\u83b7\u53d6\u8d8b\u52bf\u6570\u636e\u3002"]
      },
      contentGrowth: {
        title: "\u5185\u5bb9\u589e\u957f",
        description: "\u5206\u6790\u5185\u5bb9\u8868\u73b0\u8d8b\u52bf",
        details: [
          "\u8ffd\u8e2a\u6bcf\u4e2a\u5e16\u5b50\u7684\u6d4f\u89c8\u91cf\u3001\u70b9\u8d5e\u3001\u8bc4\u8bba\u548c\u5206\u4eab\u589e\u957f",
          "\u6bd4\u8f83\u4e0d\u540c\u65f6\u671f\u7684\u8868\u73b0",
          "\u8bc6\u522b\u53c2\u4e0e\u5ea6\u4e0a\u5347\u6216\u4e0b\u964d\u7684\u5185\u5bb9",
          "\u70b9\u51fb\u5e16\u5b50\u67e5\u770b\u8be6\u7ec6\u589e\u957f\u56fe\u8868"
        ],
        tips: ["\u4f7f\u7528\u8d8b\u52bf\u56fe\u8868\u6a21\u6001\u6846\u8bc6\u522b\u5185\u5bb9\u8868\u73b0\u6a21\u5f0f"]
      },
      posts: {
        title: "\u5e16\u5b50\u5206\u6790",
        description: "\u5168\u9762\u7684\u5e16\u5b50\u8868\u73b0\u6570\u636e",
        details: [
          "\u67e5\u770b\u6240\u6709\u5e16\u5b50\u53ca\u8be6\u7ec6\u53c2\u4e0e\u5ea6\u6307\u6807",
          "\u6309\u5e73\u53f0\u3001\u8d26\u53f7\u548c\u65e5\u671f\u8303\u56f4\u7b5b\u9009",
          "\u6309\u6d4f\u89c8\u91cf\u3001\u70b9\u8d5e\u3001\u8bc4\u8bba\u6216\u53c2\u4e0e\u7387\u6392\u5e8f",
          "\u70b9\u51fb\u5206\u6790\u5355\u4e2a\u5e16\u5b50\u7684\u8bed\u8a00\u5206\u5e03"
        ],
        tips: ["\u4e2d\u56fd\u5e73\u53f0\u5728\u6d4f\u89c8\u91cf\u6570\u636e\u4e0d\u53ef\u7528\u65f6\u663e\u793a'-'"]
      },
      comments: {
        title: "\u8bc4\u8bba\u5206\u6790",
        description: "AI\u9a71\u52a8\u7684\u8bc4\u8bba\u60c5\u611f\u548c\u5173\u952e\u8bcd\u5206\u6790",
        details: [
          "\u897f\u65b9\u5e73\u53f0\u4f7f\u7528GPT-5.2\u5206\u6790\u8bc4\u8bba",
          "\u5fae\u535a\u3001B\u7ad9\u3001\u5c0f\u7ea2\u4e66\u3001\u6296\u97f3\u4f7f\u7528\u4e2d\u6587NLP\uff08jieba\uff09",
          "\u67e5\u770b\u60c5\u611f\u5206\u5e03\uff1a\u6b63\u9762\u3001\u8d1f\u9762\u3001\u4e2d\u6027",
          "\u4ece\u8bc4\u8bba\u4e2d\u63d0\u53d6\u70ed\u95e8\u5173\u952e\u8bcd\u548c\u8bdd\u9898"
        ],
        tips: ["\u4f7f\u7528'\u6570\u636e\u5e93\u6a21\u5f0f'\u66f4\u5feb\u641c\u7d22\u4e4b\u524d\u540c\u6b65\u7684\u8bc4\u8bba"]
      },
      commentSearch: {
        title: "\u8bc4\u8bba\u641c\u7d22",
        description: "\u5728\u8bc4\u8bba\u4e2d\u641c\u7d22\u7279\u5b9a\u5173\u952e\u8bcd",
        details: [
          "\u8de8\u6240\u6709\u5e73\u53f0\u6309\u5173\u952e\u8bcd\u641c\u7d22\u8bc4\u8bba",
          "\u4e24\u79cd\u6a21\u5f0f\uff1a\u5b9e\u65f6API\u641c\u7d22\u6216\u6570\u636e\u5e93\u641c\u7d22",
          "\u540c\u6b65\u8bc4\u8bba\u5230\u6570\u636e\u5e93\u4ee5\u52a0\u5feb\u91cd\u590d\u641c\u7d22",
          "\u67e5\u770b\u8bc4\u8bba\u4e0a\u4e0b\u6587\u53ca\u5e73\u53f0\u548c\u5e16\u5b50\u4fe1\u606f"
        ],
        tips: ["\u540c\u6b65\u4e00\u6b21\u8bc4\u8bba\u540e\uff0c\u4f7f\u7528\u5feb\u901f\u6570\u636e\u5e93\u641c\u7d22\u6a21\u5f0f"]
      },
      crossPlatform: {
        title: "\u8de8\u5e73\u53f0\u5bf9\u6bd4",
        description: "\u5728\u4e0d\u540c\u5e73\u53f0\u6bd4\u8f83\u76f8\u540c\u5185\u5bb9",
        details: [
          "\u4f7f\u7528\u81ea\u52a8\u5339\u914d\u5728\u5e73\u53f0\u95f4\u67e5\u627e\u76f8\u4f3c\u5185\u5bb9",
          "\u6bd4\u8f83YouTube\uff08\u5168\u7403\uff09\u548cB\u7ad9\uff08\u4e2d\u56fd\uff09\u7684\u60c5\u611f\u5206\u6790",
          "\u67e5\u770b\u5404\u5e73\u53f0\u7684\u5171\u540c\u548c\u72ec\u7279\u5173\u952e\u8bcd",
          "\u83b7\u53d6\u5e73\u53f0\u5dee\u5f02\u7684AI\u6d1e\u5bdf"
        ],
        tips: ["\u81ea\u52a8\u5339\u914d\u5bf9\u6807\u9898\u6216\u5173\u952e\u8bcd\u76f8\u4f3c\u7684\u5e16\u5b50\u6548\u679c\u6700\u4f73"]
      },
      aiInsights: {
        title: "AI\u6d1e\u5bdf",
        description: "GPT-5.2\u9a71\u52a8\u7684\u8868\u73b0\u5efa\u8bae",
        details: [
          "\u751f\u6210\u5168\u9762\u7684\u8868\u73b0\u5206\u6790",
          "\u83b7\u53d6\u5185\u5bb9\u7b56\u7565\u5efa\u8bae",
          "\u8bc6\u522b\u5f02\u5e38\u548c\u8d8b\u52bf",
          "\u9488\u5bf9\u4e2d\u56fd\u4e0e\u897f\u65b9\u53d7\u4f17\u7684\u5e73\u53f0\u7279\u5b9a\u6d1e\u5bdf"
        ],
        tips: ["\u6bcf\u5468\u751f\u6210\u6d1e\u5bdf\u4ee5\u8ffd\u8e2a\u5185\u5bb9\u8868\u73b0\u53d8\u5316"]
      },
      settings: {
        title: "\u8bbe\u7f6e",
        description: "\u914d\u7f6eAPI\u51ed\u8bc1\u548c\u7cfb\u7edf\u9009\u9879",
        details: [
          "\u7ba1\u7406\u6240\u6709\u5e73\u53f0\u7684API\u51ed\u8bc1",
          "\u67e5\u770b\u548c\u63a7\u5236\u6bcf\u65e5\u540c\u6b65\u8ba1\u5212",
          "\u76d1\u63a7API\u72b6\u6001\uff08TikHub\u3001Just One API\uff09",
          "\u7ba1\u7406\u5458\uff1a\u521b\u5efa\u548c\u7ba1\u7406\u8bbf\u95ee\u7801"
        ],
        tips: ["\u5b9a\u671f\u68c0\u67e5API\u72b6\u6001\u4ee5\u786e\u4fdd\u6570\u636e\u540c\u6b65\u6b63\u5e38\u5de5\u4f5c"]
      },
      syncLogs: {
        title: "\u540c\u6b65\u65e5\u5fd7",
        description: "\u76d1\u63a7\u6570\u636e\u540c\u6b65\u72b6\u6001\u548c\u5386\u53f2\u8bb0\u5f55",
        details: [
          "\u67e5\u770b\u6240\u6709\u5df2\u8fde\u63a5\u8d26\u53f7\u7684\u5b9e\u65f6\u540c\u6b65\u72b6\u6001",
          "\u901a\u8fc7\u6210\u529f/\u5931\u8d25\u6307\u6807\u76d1\u63a7\u540c\u6b65\u5386\u53f2",
          "\u6309\u5e73\u53f0\u8ffd\u8e2a\u540c\u6b65\u65f6\u95f4\u3001\u6301\u7eed\u65f6\u957f\u548c\u6570\u636e\u91cf",
          "\u8bc6\u522b\u548c\u6392\u67e5\u5931\u8d25\u7684\u540c\u6b65\u64cd\u4f5c"
        ],
        tips: ["\u5982\u679c\u4eea\u8868\u76d8\u6570\u636e\u770b\u8d77\u6765\u8fc7\u65f6\uff0c\u8bf7\u68c0\u67e5\u540c\u6b65\u65e5\u5fd7"]
      },
      artistGroups: {
        title: "\u827a\u4eba\u5206\u7ec4",
        description: "\u7ba1\u7406\u8de8\u5e73\u53f0\u8d26\u53f7\u5206\u7ec4\uff0c\u7528\u4e8e\u8de8\u5e73\u53f0\u5206\u6790",
        details: [
          "\u5c06\u4e0d\u540c\u5e73\u53f0\u7684\u8d26\u53f7\u7ec4\u5408\u521b\u5efa\u827a\u4eba\u5206\u7ec4",
          "\u6ea2\u51fa\u6548\u5e94\u5206\u6790\u81f3\u5c11\u9700\u89812\u4e2a\u5e73\u53f0",
          "\u7ba1\u7406\u5206\u7ec4\u6210\u5458\uff1a\u968f\u65f6\u6dfb\u52a0\u6216\u5220\u9664\u8d26\u53f7",
          "\u5206\u7ec4\u7528\u4e8e\u6ea2\u51fa\u6548\u5e94\u5206\u6790\u548c\u62a5\u544a\u529f\u80fd"
        ],
        tips: ["\u5c06\u540c\u4e00\u827a\u4eba\u6216\u54c1\u724c\u7684\u6240\u6709\u8d26\u53f7\u5206\u7ec4\uff0c\u624d\u80fd\u8fdb\u884c\u6709\u610f\u4e49\u7684\u6ea2\u51fa\u6548\u5e94\u5206\u6790"]
      },
      spillover: {
        title: "\u6ea2\u51fa\u6548\u5e94\u5206\u6790",
        description: "\u6d4b\u91cf\u75c5\u6bd2\u5e16\u5b50\u7684\u8de8\u5e73\u53f0\u7c89\u4e1d\u5f71\u54cd",
        details: [
          "[\u76ee\u7684] \u4f30\u8ba1\u4e00\u4e2a\u5e73\u53f0\u4e0a\u7684\u75c5\u6bd2\u5e16\u5b50\u5982\u4f55\u5f71\u54cd\u540c\u4e00\u827a\u4eba\u5206\u7ec4\u4e2d\u5176\u4ed6\u5e73\u53f0\u7684\u7c89\u4e1d\u589e\u957f",
          "[\u75c5\u6bd2\u68c0\u6d4b] \u70b9\u8d5e\u6216\u6d4f\u89c8\u91cf\u8fbe\u5230\u8d26\u53f7\u8fd1\u671f\u5e73\u5747\u503c2\u500d\u4ee5\u4e0a\u5373\u4e3a\u75c5\u6bd2\u5e16(\u81f3\u5c113\u4e2a\u5e16\u5b50\u4f5c\u57fa\u51c6)\u3002\u75c5\u6bd2\u5206\u6570 = max(\u70b9\u8d5e/\u5e73\u5747, \u6d4f\u89c8/\u5e73\u5747)",
          "[\u6d4b\u91cf\u65b9\u5f0f] \u6bd4\u8f83\u76ee\u6807\u5e73\u53f0\u5728\u75c5\u6bd2\u5e16\u53d1\u5e03\u524d\u540e\u7684\u7c89\u4e1d\u5feb\u7167(\u89c2\u5bdf\u7a97\u53e37\u5929)\u3002\u89c2\u6d4b\u53d8\u5316\u91cf = \u53d1\u5e03\u540e\u7c89\u4e1d - \u53d1\u5e03\u524d\u7c89\u4e1d",
          "[\u57fa\u51c6\u8c03\u6574] \u7528\u7b49\u957f\u524d\u671f\u65e5\u5747\u589e\u957f\u7387\u7b97\u9884\u671f\u589e\u957f\u3002\u8c03\u6574\u540e\u63d0\u5347 = \u89c2\u6d4b\u53d8\u5316\u91cf - \u9884\u671f\u589e\u957f",
          "[\u7f6e\u4fe1\u5ea6\u8bc4\u5206] 0-100\u5206\u6570\u636e\u8d28\u91cf\u8bc4\u4f30\u3002\u6263\u5206: \u5feb\u7167\u95f4\u9694(\u6700\u591a-24)\u3001\u6d4b\u91cf\u5ef6\u8fdf(\u6700\u591a-18)\u3001\u7ade\u4e89\u75c5\u6bd2\u4e8b\u4ef6(\u6bcf\u4e2a\u6700\u591a-30)\u3001\u7f3a\u5c11\u57fa\u51c6(-10)\u3002\u7b49\u7ea7: 75+ High, 50-74 Medium, <50 Low",
          "[\u914d\u5bf9\u6458\u8981] \u6309\u6765\u6e90->\u76ee\u6807\u5e73\u53f0\u914d\u5bf9\u6c47\u603b: \u4e8b\u4ef6\u6570\u3001\u6b63\u9762\u6bd4\u7387\u3001\u603b/\u5e73\u5747\u8c03\u6574\u63d0\u5347",
          "[\u6218\u7565\u624b\u518c] \u8c03\u6574\u63d0\u5347\u6700\u9ad8\u76843\u4e2a\u4f18\u5148\u6d41\u5411\u3001\u8d1f\u9762\u6548\u679c\u8b66\u793a\u6d41\u5411\u3001\u76ee\u6807\u5e73\u53f0\u63a8\u8350\u5185\u5bb9\u683c\u5f0f\u7684\u8fd0\u8425\u89c4\u5219",
          "[\u6ce8\u610f] \u8fd9\u4e9b\u6570\u5b57\u662f\u65b9\u5411\u6027\u4f30\u8ba1\u800c\u975e\u56e0\u679c\u8bc1\u660e\u3002\u65b0\u95fb\u3001\u5408\u4f5c\u3001\u7b97\u6cd5\u53d8\u5316\u7b49\u5916\u90e8\u56e0\u7d20\u672a\u88ab\u8003\u8651"
        ],
        tips: ["\u505a\u6218\u7565\u51b3\u7b56\u65f6\u91cd\u70b9\u5173\u6ce8High\u7f6e\u4fe1\u5ea6\u4e14\u6b63\u9762\u6bd4\u7387\u7a33\u5b9a\u7684\u5e73\u53f0\u914d\u5bf9"]
      },
      correlation: {
        title: "\u76f8\u5173\u6027\u5206\u6790",
        description: "\u5206\u6790\u5355\u4e2a\u5e16\u5b50\u4e0e\u7c89\u4e1d\u589e\u957f\u7684\u5173\u7cfb",
        details: [
          "[\u76ee\u7684] \u6d4b\u91cf\u5355\u4e2a\u5e16\u5b50\u5bf9\u540c\u8d26\u53f7\u7c89\u4e1d\u589e\u957f\u7684\u5f71\u54cd(\u6ea2\u51fa\u6548\u5e94\u8861\u91cf\u8de8\u5e73\u53f0\u6548\u679c\uff0c\u76f8\u5173\u6027\u5206\u6790\u8861\u91cf\u540c\u8d26\u53f7\u6548\u679c)",
          "[\u5f71\u54cd\u5206\u6570] \u6bd4\u4f8b\u5f52\u56e0\u6cd5: \u91cd\u53e0\u5e16\u5b50\u5206\u7ec4\u540e\u6309\u53c2\u4e0e\u5ea6\u6743\u91cd(\u70b9\u8d5e+\u8bc4\u8bba)\u6bd4\u4f8b\u5206\u914d\u603b\u589e\u957f\uff0c\u9632\u6b62\u91cd\u590d\u8ba1\u7b97",
          "[\u57fa\u51c6\u7ebf] \u5b89\u9759\u65e5(\u65e0\u5e16\u5b50\u4e14\u524d\u540e2\u5929\u65e0\u5e16\u5b50)\u7684\u65e5\u5747\u7c89\u4e1d\u53d8\u5316\u4e3a\u81ea\u7136\u589e\u957f\u7387\u3002\u8d85\u989d\u589e\u957f = \u5b9e\u9645\u589e\u957f - \u57fa\u51c6\u9884\u671f",
          "[\u8f6c\u5316\u7387] (\u5f52\u56e0\u589e\u957f / \u6d4f\u89c8\u91cf) x 1,000 \u2014 \u6bcf1000\u6b21\u6d4f\u89c8\u7684\u7c89\u4e1d\u8f6c\u5316\u6570",
          "[\u76f8\u5173\u7cfb\u6570] \u76ae\u5c14\u900a\u7cfb\u6570(-1\u5230+1)\u8ba1\u7b97\u6d4f\u89c8\u91cf\u3001\u70b9\u8d5e\u3001\u8bc4\u8bba\u3001\u53c2\u4e0e\u7387\u4e0e\u7c89\u4e1d\u53d8\u5316\u7684\u76f8\u5173\u6027\u3002\u81f3\u5c11\u97003\u4e2a\u6570\u636e\u70b9",
          "[\u5206\u7c7b\u5206\u6790] AI(GPT-5.2)\u6309\u5e16\u5b50\u6807\u9898\u81ea\u52a8\u5206\u7c7b(MV\u3001\u8868\u6f14\u3001\u5e55\u540e\u7b49)\u540e\u6bd4\u8f83\u5404\u5206\u7c7b\u5e73\u5747\u8868\u73b0",
          "[AI\u6458\u8981] \u751f\u6210\u6267\u884c\u6458\u8981\u3001\u9876\u7ea7\u5185\u5bb9\u3001\u5e73\u53f0\u6d1e\u5bdf\u548c\u53d1\u5e03\u7b56\u7565\u5efa\u8bae\u7684\u5206\u6790\u62a5\u544a"
        ],
        tips: ["\u6bd4\u8f83\u4e0d\u540c\u5185\u5bb9\u5206\u7c7b\u7684\u5f71\u54cd\u5206\u6570\uff0c\u627e\u51fa\u6700\u80fd\u63a8\u52a8\u7c89\u4e1d\u589e\u957f\u7684\u5185\u5bb9\u7c7b\u578b"]
      },
      reports: {
        title: "\u62a5\u544a",
        description: "AI\u9a71\u52a8\u7684\u81ea\u52a8\u6ea2\u51fa\u6548\u5e94\u5206\u6790\u62a5\u544a",
        details: [
          "\u751f\u6210\u6bcf\u5468\u6216\u6bcf\u6708\u6ea2\u51fa\u6548\u5e94\u5206\u6790\u62a5\u544a",
          "\u5305\u542b\u6838\u5fc3\u8bca\u65ad\u3001\u5e73\u53f0\u6d41\u5411\u673a\u4f1a\u548c\u5185\u5bb9\u5305\u88c5\u5efa\u8bae",
          "AI(GPT-5.2)\u57fa\u4e8e\u5b9e\u9645\u6ea2\u51fa\u6570\u636e\u751f\u6210\u7b56\u7565\u62a5\u544a",
          "\u67e5\u770b\u62a5\u544a\u5386\u53f2\u5e76\u8de8\u671f\u6bd4\u8f83",
          "\u4ee5\u7ed3\u6784\u5316\u7ae0\u8282\u7684Markdown\u683c\u5f0f\u751f\u6210"
        ],
        tips: ["\u5b9a\u671f\u751f\u6210\u6708\u5ea6\u62a5\u544a\u8ffd\u8e2a\u6ea2\u51fa\u6548\u5e94\u6a21\u5f0f\u7684\u53d8\u5316\u8d8b\u52bf"]
      }
    },
    platformList: [
      { name: "YouTube", color: "#FF0000", features: "\u5b8c\u6574API" },
      { name: "TikTok", color: "#69C9D0", features: "\u5b8c\u6574API" },
      { name: "Instagram", color: "#E1306C", features: "Just One API" },
      { name: "Weibo", color: "#E6162D", features: "Just One API" },
      { name: "Xiaohongshu", color: "#FF2442", features: "TikHub API" },
      { name: "Douyin", color: "#25F4EE", features: "TikHub API" },
      { name: "Bilibili", color: "#00A1D6", features: "Just One API" }
    ]
  }
};
